import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { Capacitor } from '@capacitor/core';
import { APP_CONFIG } from '@/lib/config';

export interface VersionInfo {
  version: string;
  build: number;
  minApkVersion?: number;
  bundleUrl?: string;
  bundleHash?: string;
  releaseNotes?: string;
  forceUpdate?: boolean;
}

export interface UpdateStatus {
  available: boolean;
  downloading: boolean;
  progress: number;
  error: string | null;
  newVersion?: VersionInfo;
}

const BUNDLE_DIR = 'athenea_bundles';
const CURRENT_VERSION_FILE = 'current_version.json';

class LiveUpdateServiceClass {
  private status: UpdateStatus = {
    available: false,
    downloading: false,
    progress: 0,
    error: null,
  };

  private listeners: ((status: UpdateStatus) => void)[] = [];
  private serverUrl: string = '';

  constructor() {
    // URL del servidor de producción para actualizaciones OTA
    // En la app nativa, window.location.origin es capacitor://localhost
    // Necesitamos usar la URL real del servidor
    this.serverUrl = 'http://192.168.10.40:8080';
  }

  subscribe(callback: (status: UpdateStatus) => void) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(l => l !== callback);
    };
  }

  private notify() {
    this.listeners.forEach(l => l({ ...this.status }));
  }

  async getLocalVersion(): Promise<VersionInfo | null> {
    try {
      // Primero intenta leer versión del bundle descargado
      const result = await Filesystem.readFile({
        path: BUNDLE_DIR + '/' + CURRENT_VERSION_FILE,
        directory: Directory.Data,
        encoding: Encoding.UTF8,
      });
      return JSON.parse(result.data as string);
    } catch {
      // Si no hay bundle descargado, usar versión hardcodeada del APK
      return {
        version: APP_CONFIG.version,
        build: APP_CONFIG.versionCode,
        minApkVersion: 162,
        bundleUrl: '',
        releaseNotes: '',
        forceUpdate: false,
      };
    }
  }

  async getRemoteVersion(): Promise<VersionInfo | null> {
    try {
      const url = this.serverUrl + '/version.json?t=' + Date.now();
      const response = await fetch(url);
      if (!response.ok) return null;
      return await response.json();
    } catch {
      return null;
    }
  }

  async checkForUpdates(): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) {
      // En web, siempre usa la versión del servidor directamente
      return false;
    }

    try {
      const [local, remote] = await Promise.all([
        this.getLocalVersion(),
        this.getRemoteVersion(),
      ]);

      if (!remote || !local) return false;

      const hasUpdate = remote.build > local.build;

      this.status = {
        ...this.status,
        available: hasUpdate,
        newVersion: hasUpdate ? remote : undefined,
      };
      this.notify();

      return hasUpdate;
    } catch (error) {
      console.error('Error checking updates:', error);
      return false;
    }
  }

  async downloadAndApplyUpdate(): Promise<boolean> {
    const newVersion = this.status.newVersion;
    if (!newVersion?.bundleUrl) {
      this.status.error = 'No hay URL de bundle disponible';
      this.notify();
      return false;
    }

    this.status = { ...this.status, downloading: true, progress: 0, error: null };
    this.notify();

    try {
      const bundleUrl = newVersion.bundleUrl.startsWith('http')
        ? newVersion.bundleUrl
        : this.serverUrl + newVersion.bundleUrl;

      // Descargar bundle
      const response = await fetch(bundleUrl);
      if (!response.ok) throw new Error('Error descargando bundle');

      const blob = await response.blob();
      const base64 = await this.blobToBase64(blob);

      this.status.progress = 50;
      this.notify();

      // Guardar bundle
      await Filesystem.writeFile({
        path: BUNDLE_DIR + '/bundle.zip',
        data: base64,
        directory: Directory.Data,
      });

      this.status.progress = 75;
      this.notify();

      // Guardar versión actual
      await Filesystem.writeFile({
        path: BUNDLE_DIR + '/' + CURRENT_VERSION_FILE,
        data: JSON.stringify(newVersion),
        directory: Directory.Data,
        encoding: Encoding.UTF8,
      });

      this.status = {
        ...this.status,
        downloading: false,
        progress: 100,
        available: false,
      };
      this.notify();

      return true;
    } catch (error: any) {
      this.status = {
        ...this.status,
        downloading: false,
        error: error.message || 'Error aplicando actualización',
      };
      this.notify();
      return false;
    }
  }

  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = (reader.result as string).split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  getStatus(): UpdateStatus {
    return { ...this.status };
  }

  clearError() {
    this.status.error = null;
    this.notify();
  }
}

export const LiveUpdateService = new LiveUpdateServiceClass();
